import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";
import {
  EQUIPMENT_CATEGORIES,
  EQUIPMENT_PRODUCTS,
  equipmentBySlug,
  equipmentCategory,
  type EquipmentCategoryId,
  type EquipmentProduct,
} from "@shared/equipment";
import { EQUIPMENT_SEO, equipmentSeoForSlug } from "@shared/pageSeo";
import { api } from "../lib/api";
import { TurnstileWidget } from "../components/Turnstile";

export function EquipmentPage() {
  useRouteSeo(EQUIPMENT_SEO);
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") ?? "");
  const category = (params.get("category") ?? "") as EquipmentCategoryId | "";

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return EQUIPMENT_PRODUCTS.filter((p) => {
      if (category && p.categoryId !== category) return false;
      if (!query) return true;
      const cat = equipmentCategory(p.categoryId).label.toLowerCase();
      return p.title.toLowerCase().includes(query) || p.slug.includes(query) || cat.includes(query);
    });
  }, [category, q]);

  function setCategory(next: string) {
    const sp = new URLSearchParams(params);
    if (next) sp.set("category", next);
    else sp.delete("category");
    setParams(sp, { replace: true });
  }

  return (
    <>
      <section className="hero hero-simple">
        <div className="container">
          <p className="eyebrow">Ecological Engineering · Equipment</p>
          <h1>Catalogue for quotation, not a shop.</h1>
          <p className="lead">
            Test systems, interconnect, modules, displays and industrial supply. Request a quote
            with the item, quantity and duty — we price from current supplier and project
            conditions.
          </p>
          <div className="hero-actions">
            <a className="btn btn-primary" href="#catalogue">
              Browse the catalogue
            </a>
            <Link className="btn btn-secondary" to="/contact">
              Talk to an engineer
            </Link>
          </div>
          <p className="hero-meta">B2B request-a-quote · no cart · no public prices</p>
        </div>
      </section>

      <section className="section section-alt" id="catalogue">
        <div className="container">
          <div className="section-head">
            <div>
              <h2>Equipment catalogue</h2>
              <p>
                {EQUIPMENT_PRODUCTS.length} published items recovered from the former Ecological
                Engineering Store. Titles are as published. Photography is not yet restored — cards
                use a category mark until assets exist.
              </p>
            </div>
            <p className="muted" style={{ margin: 0 }}>
              {filtered.length} shown
            </p>
          </div>

          <div className="equipment-toolbar">
            <input
              type="search"
              placeholder="Search titles…"
              value={q}
              onChange={(e) => {
                const v = e.target.value;
                setQ(v);
                const sp = new URLSearchParams(params);
                if (v.trim()) sp.set("q", v);
                else sp.delete("q");
                setParams(sp, { replace: true });
              }}
              aria-label="Search equipment"
            />
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              aria-label="Filter by category"
            >
              <option value="">All categories</option>
              {EQUIPMENT_CATEGORIES.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>

          <div className="equipment-chips" role="tablist" aria-label="Categories">
            <button
              type="button"
              className={`equipment-chip ${!category ? "active" : ""}`}
              onClick={() => setCategory("")}
            >
              All
            </button>
            {EQUIPMENT_CATEGORIES.map((c) => (
              <button
                key={c.id}
                type="button"
                className={`equipment-chip ${category === c.id ? "active" : ""}`}
                onClick={() => setCategory(c.id)}
              >
                {c.label}
              </button>
            ))}
          </div>

          <div className="grid-3 equipment-grid">
            {filtered.map((p) => (
              <article key={p.id} className="card card-hover equipment-card">
                <div className="equipment-card-mark" aria-hidden="true">
                  <CategoryMark id={p.categoryId} />
                </div>
                <p className="eyebrow" style={{ marginBottom: "0.45rem" }}>
                  {equipmentCategory(p.categoryId).label}
                </p>
                <h3>{p.title}</h3>
                <Link className="btn btn-primary btn-sm" to={`/equipment/${p.slug}`}>
                  Request quote
                </Link>
              </article>
            ))}
          </div>
          {!filtered.length ? (
            <p className="muted" style={{ marginTop: "1.25rem" }}>
              No items match that filter. Clear search or pick another category.
            </p>
          ) : null}
        </div>
      </section>
    </>
  );
}

export function EquipmentProductPage() {
  const { slug = "" } = useParams();
  const product = equipmentBySlug(slug);
  useRouteSeo(product ? equipmentSeoForSlug(product.slug) : EQUIPMENT_SEO);

  if (!product) return <Navigate to="/equipment" replace />;

  const category = equipmentCategory(product.categoryId);

  return (
    <>
      <section className="hero hero-simple">
        <div className="container">
          <p className="eyebrow">Equipment · {category.label}</p>
          <h1>{product.title}</h1>
          <p className="lead">
            Request a quote. Include quantity, site or duty notes, and any specification you
            already hold. We do not publish list prices on this lane.
          </p>
          <div className="hero-actions">
            <a className="btn btn-primary" href="#quote">
              Request quote
            </a>
            <Link className="btn btn-secondary" to="/equipment">
              Back to catalogue
            </Link>
          </div>
        </div>
      </section>

      <section className="section" id="quote">
        <div className="container grid-2">
          <div className="card equipment-identity">
            <div className="equipment-card-mark" aria-hidden="true">
              <CategoryMark id={product.categoryId} />
            </div>
            <p className="eyebrow">{category.label}</p>
            <h2>{product.title}</h2>
            <dl className="kv">
              <div>
                <dt>Catalogue slug</dt>
                <dd>
                  <code>{product.slug}</code>
                </dd>
              </div>
              <div>
                <dt>Item id</dt>
                <dd>{product.id}</dd>
              </div>
            </dl>
            <p className="muted" style={{ marginTop: "1rem" }}>
              Recovered title only — long product copy and photography are out of scope for this
              release. Quotes are prepared against the current supplier book and the duty you
              describe.
            </p>
          </div>
          <EquipmentQuoteForm product={product} />
        </div>
      </section>
    </>
  );
}

function EquipmentQuoteForm({ product }: { product: EquipmentProduct }) {
  const [form, setForm] = useState({
    fullName: "",
    organisation: "",
    email: "",
    phone: "",
    quantity: "1",
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileAttempt, setTurnstileAttempt] = useState(0);
  const onTurnstileToken = useCallback((t: string | null) => setTurnstileToken(t), []);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const qty = form.quantity.trim() || "1";
      const notes = form.notes.trim();
      const message = [
        "EQUIPMENT QUOTE REQUEST",
        `Product: ${product.title}`,
        `Slug: ${product.slug}`,
        `Catalogue id: ${product.id}`,
        `Category: ${equipmentCategory(product.categoryId).label}`,
        `Quantity: ${qty}`,
        notes ? `Notes:\n${notes}` : "Notes: (none)",
      ].join("\n");

      await api.enquiry({
        fullName: form.fullName,
        email: form.email,
        companyName: form.organisation,
        phone: form.phone,
        serviceInterest: `Equipment — ${product.title}`.slice(0, 120),
        message,
        source: "ecoeng.net/equipment",
        "cf-turnstile-response": turnstileToken ?? "",
      });
      setOk(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send");
      setTurnstileToken(null);
      setTurnstileAttempt((n) => n + 1);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="card" onSubmit={(e) => void onSubmit(e)}>
      {ok ? (
        <div className="alert alert-success">
          Quote request received. We will follow up by email with availability and pricing for{" "}
          <strong>{product.title}</strong>.
        </div>
      ) : null}
      {error ? <div className="alert alert-error">{error}</div> : null}

      <p className="eyebrow">Request a quote</p>
      <h3 style={{ marginBottom: "1rem" }}>{product.title}</h3>

      <label>
        Quantity
        <input
          value={form.quantity}
          onChange={(e) => setForm({ ...form, quantity: e.target.value })}
          inputMode="numeric"
        />
      </label>
      <label>
        Full name
        <input
          required
          value={form.fullName}
          onChange={(e) => setForm({ ...form, fullName: e.target.value })}
        />
      </label>
      <label>
        Organisation
        <input
          required
          value={form.organisation}
          onChange={(e) => setForm({ ...form, organisation: e.target.value })}
        />
      </label>
      <label>
        Email
        <input
          type="email"
          required
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
        />
      </label>
      <label>
        Phone <span className="muted">(optional)</span>
        <input
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
        />
      </label>
      <label>
        Notes
        <textarea
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          placeholder="Duty, specification, site, delivery window…"
        />
      </label>
      <TurnstileWidget key={turnstileAttempt} onToken={onTurnstileToken} />
      <button className="btn btn-primary" type="submit" disabled={busy || ok}>
        {busy ? "Sending…" : "Request quote"}
      </button>
      <p className="muted" style={{ marginTop: "0.85rem", fontSize: "0.85rem" }}>
        Goes to info@ecoeng.net. This is not an order and does not take payment.
      </p>
    </form>
  );
}

function useRouteSeo(seo: { title: string; description: string; canonicalPath: string }) {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = seo.title;
    const desc = document.head.querySelector('meta[name="description"]');
    const prevDesc = desc?.getAttribute("content") ?? null;
    desc?.setAttribute("content", seo.description);
    let canonical = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    const createdCanonical = !canonical;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    const prevCanonical = canonical.getAttribute("href");
    canonical.setAttribute("href", `https://ecoeng.net${seo.canonicalPath}`);
    return () => {
      document.title = previousTitle;
      if (prevDesc != null) desc?.setAttribute("content", prevDesc);
      if (createdCanonical) canonical?.remove();
      else if (prevCanonical) canonical?.setAttribute("href", prevCanonical);
    };
  }, [seo]);
}

function CategoryMark({ id }: { id: EquipmentCategoryId }) {
  return (
    <svg viewBox="0 0 72 72" fill="none" aria-hidden="true">
      <rect width="72" height="72" rx="16" fill="#0B1426" />
      {id === "test-equipment" || id === "manufactured-equipment" ? (
        <>
          <rect x="16" y="22" width="40" height="28" rx="4" stroke="#FF6B00" strokeWidth="2.2" />
          <path d="M24 36h24M36 28v16" stroke="#FF8533" strokeWidth="2" />
        </>
      ) : id === "services" ? (
        <path
          d="M22 48V24l14-6 14 6v24l-14 6-14-6z"
          stroke="#FF6B00"
          strokeWidth="2.2"
          fill="none"
        />
      ) : id === "connectors" || id.includes("cables") ? (
        <>
          <path d="M16 36h40" stroke="#FF6B00" strokeWidth="3" strokeLinecap="round" />
          <circle cx="22" cy="36" r="5" stroke="#FF8533" strokeWidth="2" />
          <circle cx="50" cy="36" r="5" stroke="#FF8533" strokeWidth="2" />
        </>
      ) : id === "power-supplies" ? (
        <path d="M34 16l-8 22h10l-6 18 20-26H38l8-14H34z" fill="#FF6B00" />
      ) : id === "digital-displays" || id === "pos-systems" || id === "touch-screen-panels" ? (
        <rect x="14" y="20" width="44" height="32" rx="4" stroke="#FF6B00" strokeWidth="2.2" />
      ) : id === "safety-ppe" ? (
        <path
          d="M36 16c8 8 14 14 14 24a14 14 0 11-28 0c0-10 6-16 14-24z"
          stroke="#FF6B00"
          strokeWidth="2.2"
        />
      ) : id === "antennas-iot" || id === "professional-drone-systems" ? (
        <>
          <circle cx="36" cy="40" r="6" stroke="#FF6B00" strokeWidth="2.2" />
          <path d="M36 16v18M24 24c8-8 16-8 24 0" stroke="#FF8533" strokeWidth="2" />
        </>
      ) : (
        <rect x="20" y="20" width="32" height="32" rx="6" stroke="#FF6B00" strokeWidth="2.2" />
      )}
    </svg>
  );
}
